import { useEffect, useState } from 'react'
import { Bell } from 'lucide-react'
import ListaNotificacoes from './ListaNotificacoes'
import {
  notificacoesApi,
  type Frequencia,
  type Preferencias,
  type TipoDeNotificacao,
} from './notificacoesApi'

/**
 * A página das notificações: a lista (é por aqui que se vêem no
 * telemóvel, onde o sino não cabe na barra de topo) e as preferências,
 * a frequência do EMAIL por tipo de acontecimento. O sino recebe sempre;
 * "Nunca" só desliga o email.
 */
export default function PaginaNotificacoes() {
  const [prefs, setPrefs] = useState<Preferencias | null>(null)
  const [escolhas, setEscolhas] = useState<Partial<Record<TipoDeNotificacao, Frequencia>>>({})
  const [estado, setEstado] = useState<'pronto' | 'a-gravar' | 'gravado'>('pronto')
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    notificacoesApi
      .preferencias()
      .then((p) => {
        if (!vivo) return
        setPrefs(p)
        setEscolhas(p.frequencias)
      })
      .catch((e: unknown) => {
        if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível ler as preferências.')
      })
    return () => {
      vivo = false
    }
  }, [])

  const gravar = async () => {
    setEstado('a-gravar')
    setErro(null)
    try {
      const p = await notificacoesApi.gravarPreferencias(escolhas)
      setPrefs(p)
      setEscolhas(p.frequencias)
      setEstado('gravado')
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível gravar as preferências.')
      setEstado('pronto')
    }
  }

  const mudou = !!prefs && Object.entries(escolhas).some(([t, f]) => prefs.frequencias[t as TipoDeNotificacao] !== f)

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-auto bg-[var(--color-bg)] text-[var(--color-text)]">
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <h1 className="flex items-center gap-2 text-base font-semibold">
          <Bell size={16} className="text-[var(--color-accent)]" aria-hidden />
          Notificações
        </h1>
        <ListaNotificacoes
          limite={50}
          className="mt-3 max-h-[50vh] rounded border border-[var(--color-border)] bg-[var(--color-panel)]"
        />
        <h2 className="mt-6 text-sm font-semibold">Preferências do email</h2>
        <p className="mt-1 text-xs leading-relaxed text-[var(--color-text-muted)]">
          As notificações aparecem sempre no sino da barra de topo. Aqui escolhe quando as recebe
          também por email{prefs ? ` (${prefs.email})` : ''}: imediatamente (agrupadas em janelas de
          alguns minutos), num resumo diário às 8h, num resumo semanal à segunda-feira, ou nunca.
          Os resumos só são enviados quando há alguma notificação. Cada email tem uma ligação para
          deixar de o receber.
        </p>
        {erro && <p className="mt-3 text-xs text-[var(--color-error-text)]">{erro}</p>}
        {!prefs && !erro && <p className="mt-4 text-xs text-[var(--color-text-muted)]">A carregar…</p>}
        {prefs && (
          <>
            <table className="mt-4 w-full border-collapse text-xs">
              <caption className="sr-only">Frequência do email por tipo de notificação</caption>
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-text-dim)]">
                  <th scope="col" className="py-2 pr-3 font-medium">Notificação</th>
                  {prefs.possiveis.map((f) => (
                    <th key={f.valor} scope="col" className="px-2 py-2 text-center font-medium">
                      {f.nome}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prefs.tipos.map((t) => (
                  <tr key={t.tipo} className="border-b border-[var(--color-border)]">
                    <th scope="row" className="py-2 pr-3 text-left font-normal">
                      <span className="block font-medium">{t.nome}</span>
                      <span className="block text-[11px] text-[var(--color-text-muted)]">{t.descricao}</span>
                    </th>
                    {prefs.possiveis.map((f) => (
                      <td key={f.valor} className="px-2 py-2 text-center">
                        <input
                          type="radio"
                          name={`freq-${t.tipo}`}
                          value={f.valor}
                          checked={(escolhas[t.tipo] ?? prefs.por_omissao) === f.valor}
                          onChange={() => {
                            setEscolhas((e) => ({ ...e, [t.tipo]: f.valor }))
                            setEstado('pronto')
                          }}
                          aria-label={`${t.nome}: ${f.nome}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => void gravar()}
                disabled={!mudou || estado === 'a-gravar'}
                className="rounded bg-[var(--color-accent)] px-3 py-1 text-xs font-medium text-[var(--color-bg)] disabled:opacity-50"
              >
                {estado === 'a-gravar' ? 'A gravar…' : 'Gravar'}
              </button>
              {estado === 'gravado' && !mudou && (
                <span className="text-xs text-[var(--color-text-muted)]" role="status">
                  Preferências gravadas.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
